import type {
  QueryParamsDto,
  QueryParamsFilter,
  SortDirection,
} from "@mimber/schema"

const TABLE_STATE_NAMESPACE = "mimber-organization:table-state"
const TABLE_STATE_VERSION = "v1"

interface RememberedTableStateKeyInput {
  tableId: string
  organizationId?: string | null
  scope?: Record<string, string | number | null | undefined>
}

const isPositiveNumber = (value: unknown): value is number => {
  return Number.isInteger(value) && Number(value) > 0
}

const sanitizeSort = (sort: unknown): QueryParamsDto["sort"] | undefined => {
  if (!sort || typeof sort !== "object" || Array.isArray(sort)) {
    return undefined
  }

  const sortEntries = Object.entries(sort).filter(
    ([field, direction]) =>
      Boolean(field) && (direction === "asc" || direction === "desc")
  ) as [string, SortDirection][]

  if (sortEntries.length === 0) {
    return undefined
  }

  return Object.fromEntries(sortEntries)
}

const sanitizeFilters = (filters: unknown): QueryParamsFilter | undefined => {
  if (!filters || typeof filters !== "object" || Array.isArray(filters)) {
    return undefined
  }

  return filters as QueryParamsFilter
}

export const sanitizeRememberedQueryParams = (
  value: unknown,
  defaults: QueryParamsDto
): QueryParamsDto => {
  const base: QueryParamsDto = {
    ...defaults,
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return base
  }

  const source = value as QueryParamsDto

  if (isPositiveNumber(source.page)) {
    base.page = source.page
  }

  if (isPositiveNumber(source.limit)) {
    base.limit = source.limit
  }

  if (typeof source.search === "string") {
    base.search = source.search
  }

  const sort = sanitizeSort(source.sort)
  if (sort) {
    base.sort = sort
  }

  const filters = sanitizeFilters(source.filters)
  if (filters) {
    base.filters = filters
  }

  return base
}

export const buildRememberedTableStateStorageKey = ({
  tableId,
  organizationId,
  scope,
}: RememberedTableStateKeyInput): string => {
  const organizationSegment = organizationId || "unknown-org"
  const scopeEntries = Object.entries(scope ?? {})
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${String(value)}`)

  const scopeSegment =
    scopeEntries.length > 0 ? `:${scopeEntries.join("|")}` : ""

  return `${TABLE_STATE_NAMESPACE}:${TABLE_STATE_VERSION}:${organizationSegment}:${tableId}${scopeSegment}`
}
